import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IonContent } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { Api } from '../../../smoothr-web-app-core/api/api';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { SendObjectionCommand } from '../../../smoothr-web-app-core/models/SendObjectionCommand';
import { NavigationService } from 'src/app/services/navigation.service';
import { environment } from 'src/environments/environment';
import { getPhoneFormValue, phoneValidatorForGermany, phoneValidatorForLibrary } from 'src/app/utils/customer-profile.utils';
import { TurnstileComponent } from 'src/app/components/turnstile/turnstile.component';

const WITHDRAWAL_PERIOD_DAYS = 14;
const DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'DD.MM.YYYY';
const DISPLAY_DATE_TIME_FORMAT = 'DD.MM.YYYY, HH:mm';
const VENUE_COUNTRY = 'de';

type WithdrawalSummary = Omit<SendObjectionCommand, 'captcha' | 'venue' | 'date'> & {
	venueName: string;
	date: string;
	submittedAt: string;
};

@Component({
	selector: 'app-withdrawal',
	templateUrl: './withdrawal.page.html',
	styleUrls: ['./withdrawal.page.scss'],
})
export class WithdrawalPage implements OnInit {
	static url = 'widerruf';

	@ViewChild(TurnstileComponent) turnstile: TurnstileComponent;
	@ViewChild(IonContent) content: IonContent;

	form: FormGroup;
	venues: Venue[] = [];
	venuePopoverOptions = {
		size: 'cover',
		cssClass: 'withdrawal-venue-popover',
	};
	loadingVenues = false;
	loading = false;
	submitted = false;
	summary: WithdrawalSummary | null = null;
	captchaToken: string | null = null;
	minDate = '';
	maxDate = '';

	constructor(
		private repository: RepositoryService,
		private navigationService: NavigationService,
		private location: Location,
		private snackbarCtrl: MatSnackBar,
		private translate: TranslateService
	) {}

	ngOnInit() {
		this.updateDateRange();
		this.form = this.createForm();
		this.loadVenues();
	}

	createForm(): FormGroup {
		const customer = this.repository.customer && !this.repository.customer.isAnonymous ? this.repository.customer : null;

		return new FormGroup({
			firstName: new FormControl(customer?.firstName ?? '', [
				Validators.required,
				Validators.pattern(/^[^0-9]*$/),
				Validators.maxLength(100),
			]),
			lastName: new FormControl(customer?.lastName ?? '', [
				Validators.required,
				Validators.pattern(/^[^0-9]*$/),
				Validators.maxLength(100),
			]),
			email: new FormControl(customer?.email ?? '', [
				Validators.required,
				Validators.maxLength(254),
				Validators.pattern(
					"^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$"
				),
			]),
			phone: new FormControl(getPhoneFormValue(customer?.phone), [
				this.phoneRequiredValidator,
				phoneValidatorForLibrary,
				phoneValidatorForGermany,
			]),
			venue: new FormControl('', [Validators.required]),
			date: new FormControl('', [Validators.required, this.dateRangeValidator]),
			orderNumber: new FormControl('', [Validators.required, Validators.maxLength(50), Validators.pattern(/^[\p{L}\p{N} .#-]+$/u)]),
			comment: new FormControl('', [Validators.maxLength(500)]),
		});
	}

	async loadVenues() {
		this.loadingVenues = true;
		try {
			const venues = await this.repository.getAllVenues(environment.customerGroup);
			this.venues = venues
				.filter(venue => venue.isPublished && !venue.deletedAt && venue.country?.toLowerCase() === VENUE_COUNTRY)
				.sort((a, b) => a.name.localeCompare(b.name));
		} catch (e) {
			console.error(e);
			this.snackbarCtrl.open(this.translate.instant('errors.data_error'), '', {
				duration: 4000,
			});
		}
		this.loadingVenues = false;
	}

	private updateDateRange() {
		this.maxDate = moment().format(DATE_FORMAT);
		this.minDate = moment().subtract(WITHDRAWAL_PERIOD_DAYS, 'days').format(DATE_FORMAT);
	}

	private phoneRequiredValidator(control: AbstractControl): ValidationErrors | null {
		return control.value?.phone ? null : { required: true };
	}

	private dateRangeValidator = (control: AbstractControl): ValidationErrors | null => {
		if (!control.value) {
			return null;
		}
		const date = moment(control.value, DATE_FORMAT, true);
		if (!date.isValid() || control.value < this.minDate || control.value > this.maxDate) {
			return { dateRange: true };
		}
		return null;
	};

	isRequiredError(controlName: string): boolean {
		const control = this.form?.get(controlName);
		return !!control?.hasError('required') && (control.touched || control.dirty);
	}

	isPatternError(controlName: string): boolean {
		const control = this.form?.get(controlName);
		return !!control?.errors && !control.hasError('required') && (control.touched || control.dirty);
	}

	getControlValue(controlName: string) {
		return this.form?.get(controlName)?.value;
	}

	onCaptchaToken(token: string | null) {
		this.captchaToken = token;
	}

	async submit() {
		this.updateDateRange();
		this.form.get('date').updateValueAndValidity();
		if (this.form.invalid || !this.captchaToken || this.loading) {
			this.form.markAllAsTouched();
			return;
		}

		const phone = this.getControlValue('phone');
		const comment = this.getControlValue('comment')?.trim();
		const body: SendObjectionCommand = {
			firstName: this.getControlValue('firstName').trim(),
			lastName: this.getControlValue('lastName').trim(),
			email: this.getControlValue('email').trim(),
			phoneNumber: phone.country + phone.phone,
			venue: this.getControlValue('venue'),
			date: this.getControlValue('date'),
			orderNumber: this.getControlValue('orderNumber').trim(),
			comment: comment || null,
			captcha: this.captchaToken,
		};

		this.loading = true;
		try {
			await Api.sendObjection(body);
			const { captcha, venue, date, ...sent } = body;
			this.summary = {
				...sent,
				venueName: this.venues.find(it => it._id === venue)?.name ?? '',
				date: moment(date, DATE_FORMAT).format(DISPLAY_DATE_FORMAT),
				submittedAt: moment().format(DISPLAY_DATE_TIME_FORMAT),
			};
			this.submitted = true;
			this.content?.scrollToTop(300);
		} catch (e) {
			console.error(e);
			this.turnstile?.reset();
			this.snackbarCtrl.open(this.translate.instant('withdrawal.error'), '', {
				duration: 4000,
			});
		}
		this.loading = false;
	}

	async goHome() {
		await this.navigationService.home();
	}

	async goBack() {
		if (this.location) {
			this.location.back();
			return;
		}

		if (!this.repository._order) {
			await this.navigationService.home();
		} else {
			await this.navigationService.menu();
		}
	}
}
